import { type CannedFrame } from "./cannedReport.ts";
import { mountReportsExperience } from "./reportsExperience.ts";
import { mountReportBuilderFlow } from "./reportBuilderFlow.ts";
import type { SavedRef } from "./reportsExperienceModel.ts";
import type { ReportAudienceSave } from "./reportSharingModel.ts";
import { mountSavedReportResults } from "./reportBuilderResults.ts";
import { locationViewCards, type ExternalReportCard } from "./reportsExperienceModel.ts";
import { locationDashboardSource } from "./reportsDashboardLocation.ts";
import { cannedReportRequest, cannedReportFile, setSavedReportAudience } from "./api.ts";
// RPT1 (docs/build-briefs/RPT1-owner-reports-slice-1.md; PRD P0.13; DEC-268;
// design/mockups/core-owner-reports-s1-{day,items,tips}.html): the Reports
// group in the admin console. Three reports, Sales by day, Sales by item and
// Tips by employee, each with the same four things (a date range, a
// freshness strip, a tie-out line, a Download CSV button), plus the three
// existing reviews (Closeout Summary, Void History, Manager Exceptions),
// which moved here from Settings > Operations with their ids and handlers
// unchanged (main.ts still owns them).
//
// This module owns everything inside #admin-group-panel-reports and nothing
// else. admin.ts hands off to openReports on entering the group and calls
// resetReports from invalidate() so a shared tablet's next employee never sees
// the last one's figures. Admin surface: a pointer and keyboard are assumed,
// but every control is still a 44px button or a native date input, there is no
// hover affordance, and nothing leans on a title tooltip.
//
// Authorization is the server's. This module only decides what to OFFER:
// each report's entry appears with its own permission, and the Download CSV
// button with report.export. Tips by employee is offered to anyone who can
// reach the group, because every employee may see their own tips (the server
// answers with just their rows).

import { hideHoverHelp } from "./hoverHelp.ts";
import { reportNavigation } from "./reportHome.ts";
import { discountKindLabel } from "./discountKind.ts";
import { createExportMenu } from "./exportMenu.ts";
import { customReportExportTarget, openCustomReport, resetCustomReport } from "./customReport.ts";
import { mayExport } from "./customReportModel.ts";
import { type GateExportFormat } from "./exportMenuModel.ts";
import { deliverExportFile } from "./googleSheets.ts";
import {
  exportCustomReport,
  exportSavedReport,
  getReportContext,
  getReportLocations,
  getReportCsv,
  getPayrollPayPeriods,
  getDiscountKindReport,
  getDiscountKindFile,
  getSalesByDayReport,
  getCostMarginCsv,
  getCostMarginReport,
  getMenuProfitabilityCsv,
  getMenuProfitabilityReport,
  putMenuProfitabilityTarget,
  getSalesTaxWorksheetCsv,
  getRoyaltyCharges,
  getRoyaltyChargesFile,
  getRoyaltyStatement,
  postRoyaltyDispute,
  getSalesTaxWorksheetReport,
  putSalesTaxFilingSettings,
  getAbcSpiritsReport,
  getAbcSpiritsFile,
  type AbcSpiritsReportDto,
  type ReportContextDto,
  type ReportId,
  type PlanningLocationDto,
  type ReportMetaDto,
  type ReportTieOutDto,
  type DiscountKindReportDto,
  type SalesByDayReportDto,
  type CostMarginQuery,
  type CostMarginReportDto,
  type MenuProfitabilityReportDto,
} from "./api.ts";
import { showTenderColumns } from "./closeoutCashModel.ts";
import { zoneFriendlyName } from "./discountSpecialEditor.ts";
import {
  RANGE_PRESETS,
  USAGE_WASTE_PRESETS,
  amountCell,
  avgTicketText,
  cutoffText,
  dayLabel,
  formatCount,
  formatMoney,
  inclusiveDayCount,
  percentChange,
  rangeForPreset,
  reductionCell,
  weekdayLabel,
  type MoneyCell,
  type PercentChange,
  type RangePreset,
} from "./reportFormat.ts";
import { capitalizeServerMessage } from "./serverMessageDisplay.ts";
// UX108: the report on screen and its range are part of the screen's address, so a refresh reopens the same one.
import { requestRoutePublish } from "./routeHistory.ts";
import { ROUTE_RANGE_PRESETS, type ReportRange } from "./screenRoute.ts";
import { hasAnyPermission, hasPermission, loadTerminalConfig, type Session } from "./session.ts";
import { renderCostMargin, type CostMarginFilterState } from "./costMargin.ts";
import { renderMenuProfitability, type MenuProfitabilityView } from "./menuProfitability.ts";
import { MP_DEFAULT_PRESET, MP_PRESETS, nextQuadrantFilter } from "./menuProfitabilityModel.ts";
import { installScrollCues } from "./scrollCue.ts";
import { renderSalesTaxWorksheet } from "./salesTaxWorksheet.ts";
import { renderAbcSpirits } from "./abcSpirits.ts";
import { shiftMonth } from "./abcSpiritsModel.ts";
import { renderLocationRoyalty } from "./locationRoyaltyPanel.ts";
import { isLedgerShape, type LocationLedgerDto } from "./royaltyChargesModel.ts";
import type { RoyaltyStatementDto } from "./royaltyStatementModel.ts";
import {
  customSelection,
  describeWorksheetRefusal,
  kindFilingFrequency,
  periodQuery,
  selectionFromReport,
  shiftSelection,
  switchKind,
  WORKSHEET_NEVER_FILES,
  type PeriodKind,
  type PeriodSelection,
  type SalesTaxReportDto,
  type WorksheetBasis,
} from "./salesTaxWorksheetModel.ts";

function mustFind<T extends Element>(selector: string): T {
  const element = document.querySelector<T>(selector);
  if (element === null) {
    throw new Error(`expected element for selector "${selector}"`);
  }
  return element;
}

const viewEl = mustFind<HTMLElement>("#reports-view");
const hintEl = mustFind<HTMLElement>("#reports-hint");
const titleEl = mustFind<HTMLElement>("#reports-title");
const subEl = mustFind<HTMLElement>("#reports-sub");
const locationWrapEl = mustFind<HTMLElement>("#reports-location-wrap");
const locationSelectEl = mustFind<HTMLSelectElement>("#reports-location");
const downloadButton = mustFind<HTMLButtonElement>("#reports-download");
const downloadStatusEl = mustFind<HTMLElement>("#reports-download-status");
const backButton = mustFind<HTMLButtonElement>("#reports-back");

/** EXP1-S1 (DEC-402): the reports on the export gate, which offer the Export menu (CSV and Excel). The rest keep their
 * one-tap CSV button until their own slice (EXP1-S3 to S5). */
const EXPORT_MENU_REPORTS: ReadonlySet<ReportId> = new Set<ReportId>([
  "sales-by-day",
  "royalty-statement",
  "cost-margin",
  "menu-profitability",
  "sales-tax",
  "abc-spirits",
  // CRB1: the custom report's file is the report on screen, through the same gate.
  "custom",
]);

/** EXP1-S3: the reports that were several CSV files offer one CSV per sheet; Excel and PDF are the whole report. */
const SHEET_CHOICES: Partial<Record<ReportId, readonly { id: string; label: string }[]>> = {
  "abc-spirits": [
    { id: "brand", label: "Brand and size" },
    { id: "size", label: "Form by size" },
    { id: "figures", label: "Form figures" },
  ],
  "cost-margin": [
    { id: "drinks", label: "Cost and margin" },
    { id: "days", label: "By day" },
  ],
};

function downloadLabelFor(report: ReportId | undefined): string {
  return report !== undefined && EXPORT_MENU_REPORTS.has(report) ? "Export" : "Download CSV";
}

const exportMenu = createExportMenu({
  trigger: downloadButton,
  testIdPrefix: "reports-export",
  googleSheet: () => state.session,
  menuActive: () => state.report !== undefined && EXPORT_MENU_REPORTS.has(state.report),
  onPick: (format, sheet) => void handleDownload(format, sheet),
  sheetChoices: () => (state.report === undefined ? [] : (SHEET_CHOICES[state.report] ?? [])),
  onPlain: () => void handleDownload("csv"),
});
const rangebarEl = mustFind<HTMLElement>("#reports-rangebar");
const rangeChipsEl = mustFind<HTMLElement>("#reports-range-chips");
const rangeSpanEl = mustFind<HTMLElement>("#reports-range-span");
const customRowEl = mustFind<HTMLElement>("#reports-custom");
const customFromInput = mustFind<HTMLInputElement>("#reports-custom-from");
const customToInput = mustFind<HTMLInputElement>("#reports-custom-to");
const customApplyButton = mustFind<HTMLButtonElement>("#reports-custom-apply");
const customErrorEl = mustFind<HTMLElement>("#reports-custom-error");
const freshEl = mustFind<HTMLElement>("#reports-fresh");
const statusEl = mustFind<HTMLElement>("#reports-status");
const bodyEl = mustFind<HTMLElement>("#reports-body");
installScrollCues(bodyEl);

const NAV_BUTTONS: Record<ReportId, HTMLButtonElement> = {
  "sales-by-day": mustFind<HTMLButtonElement>("#reports-nav-sales-by-day"),
  "cost-margin": mustFind<HTMLButtonElement>("#reports-nav-cost-margin"),
  "sales-tax": mustFind<HTMLButtonElement>("#reports-nav-sales-tax"),
  "abc-spirits": mustFind<HTMLButtonElement>("#reports-nav-abc-spirits"),
  "menu-profitability": mustFind<HTMLButtonElement>("#reports-nav-menu-profitability"),
  "royalty-statement": mustFind<HTMLButtonElement>("#reports-nav-royalty-statement"),
  custom: mustFind<HTMLButtonElement>("#reports-nav-custom"),
};

const sharedReportNavigation = reportNavigation([
  {id:"sales",label:"Sales",entries:(["sales-by-day","sales-tax","royalty-statement","menu-profitability"] as ReportId[]).map(id=>({id,button:NAV_BUTTONS[id]}))},
  {id:"inventory",label:"Inventory",entries:(["cost-margin","abc-spirits"] as ReportId[]).map(id=>({id,button:NAV_BUTTONS[id]}))},
  {id:"custom-group",label:"Custom",entries:[{id:"custom",button:NAV_BUTTONS.custom}]},
],undefined);
mustFind<HTMLElement>('[data-testid="reports-nav"]').replaceWith(sharedReportNavigation.element);
const cannedHost = document.createElement("div");cannedHost.hidden=true;viewEl.after(cannedHost);
const cannedNavigationButton=document.createElement("button");cannedNavigationButton.type="button";cannedNavigationButton.className="chip";cannedNavigationButton.textContent="Reports home";
sharedReportNavigation.element.prepend(cannedNavigationButton);
let cannedFrame:CannedFrame|undefined;
let cannedVisible=false;
cannedNavigationButton.onclick=()=>void openCannedCatalog();
// UIFIX1 (DEC-500, SF-6): an older report returns to the Reports home through the same back link every report has.
backButton.onclick=()=>void openCannedCatalog();
/** RPTLAYOUT1-F1-S3: the Reports frame shows the home, the guided builder, or a saved custom report's results. */
type CannedScreen={kind:"home"}|{kind:"builder";editing?:{spec:unknown;id:string;baseVersion:number}}|{kind:"results";id:string;version?:number};
async function openCannedCatalog(screen:CannedScreen={kind:"home"}):Promise<void>{
  const captured=state.session;if(captured===undefined)return;
  state.generation++;resetCustomReport();cannedFrame?.destroy();cannedVisible=true;cannedHost.hidden=false;viewEl.hidden=true;hintEl.hidden=true;
  const configured=loadTerminalConfig();const siteId=configured?.tenantId===captured.tenantId?configured.locationId:state.locationId;
  if(siteId===undefined){cannedHost.textContent="No authorized location is selected.";return;}
  const prefix=`/tenants/${encodeURIComponent(captured.tenantId)}/reports`;
  const openOnLoad=pendingCannedOpen;
  const openSavedResults=(ref:SavedRef):void=>{
    // RPTMINE1: a saved report opens straight into its results; a template opens in the template screen, which runs it.
    if(ref.kind==="saved")void openCannedCatalog({kind:"results",id:ref.id});
    else{pendingTemplateId=ref.id;void showReport("custom");}
  };
  // RPTLAYOUT1-F1-S1: the home hides the rail (its report names are cards now) and leaves the rest of this view as it was.
  sharedReportNavigation.element.hidden=true;setMenuBackHidden(true);
  const shared={prefix:`${prefix}/canned`,customPrefix:`${prefix}/custom`,locationId:siteId,marksPrefix:`${prefix}/marks`,
    active:()=>cannedVisible&&state.session?.token===captured.token&&state.session?.tenantId===captured.tenantId,
    request:<T>(method:"GET"|"POST",path:string,body?:unknown)=>cannedReportRequest<T>(captured.apiBaseUrl,path,method,body),
    file:(path:string,body:unknown)=>cannedReportFile(captured.apiBaseUrl,path,body),
    setAudience:(id:string,audience:ReportAudienceSave)=>setSavedReportAudience(captured.apiBaseUrl,captured.tenantId,id,audience),
  };
  // CLOSEFIX1: the location's open pay periods (its payroll connection) for the Tips by employee dates; only for a person who may read them.
  const payPeriods=hasPermission(captured,"payroll.tips.send")?async()=>{
    const reply=await getPayrollPayPeriods(captured.apiBaseUrl,captured.tenantId);
    return reply.status===200&&Array.isArray(reply.body.periods)?reply.body.periods.map(period=>({periodStart:period.periodStart,periodEnd:period.periodEnd})):[];
  }:undefined;
  const flowHost={...shared,kind:"location" as const,can:(permission:string)=>hasPermission(captured,permission),back:()=>void openCannedCatalog(),
    // REPORTUX1: Edit report on a saved report and on the wizard's done screen needs the same permission as building one.
    ...(hasPermission(captured,"report.custom.build")?{editReport:(editing:{spec:unknown;id:string;baseVersion:number})=>void openCannedCatalog({kind:"builder",editing})}:{})};
  if(screen.kind==="builder"){
    const frame=mountReportBuilderFlow(cannedHost,{...flowHost,openSaved:(id,version)=>void openCannedCatalog({kind:"results",id,version}),
      ...(screen.editing===undefined?{}:{editing:screen.editing})});
    cannedFrame={destroy:()=>frame.destroy(),refresh:async()=>{}};
    return;
  }
  if(screen.kind==="results"){
    const frame=mountSavedReportResults(cannedHost,flowHost,{id:screen.id,version:screen.version});
    cannedFrame={destroy:()=>frame.destroy(),refresh:async()=>{}};
    return;
  }
  cannedFrame=mountReportsExperience(cannedHost,{...shared,
    ...(payPeriods===undefined?{}:{payPeriods}),
    // The builder is a permission read: building a report needs report.custom.build.
    ...(hasPermission(captured,"report.custom.build")?{newCustomReport:()=>void openCannedCatalog({kind:"builder"})}:{}),
    ...(hasPermission(captured,"report.custom.view")?{saved:{templates:true,open:openSavedResults}}:{}),
    // Kept until the home has used it: a permission refresh that remounts the home first must not lose the request.
    ...(openOnLoad===undefined?{}:{openOnLoad,usedOnLoad:()=>{pendingCannedOpen=undefined;}}),
    externalCards:locationExternalCards(captured),
    openExternal:id=>void showReport(id as ReportId),
    // REPORTDASH1: the dashboard above the library. Each part is the server's, only for what this person's permissions allow.
    dashboard:{
      source:locationDashboardSource({storageKey:`location:${captured.tenantId}:${captured.userId}`,prefix,locationId:siteId,
        request:<T>(method:"GET",path:string)=>cannedReportRequest<T>(captured.apiBaseUrl,path,method),
        can:permissions=>hasAnyPermission(captured,permissions)}),
      // An attention row's fix is a screen of the location console, opened by its address (main.ts checks the permission again).
      openAction:id=>{if(id.startsWith("#/"))window.location.hash=id;},
    },
  });
}
/** RPTCARDS1: the four older views with no canned counterpart are cards on the home, each behind its own permission and each with a ? help. */
function locationExternalCards(session:Session):ExternalReportCard[]{
  return locationViewCards(permission=>hasPermission(session,permission));
}


/** The permission each report's entry needs. */
const REPORT_PERMISSION: Partial<Record<ReportId, string>> = {
  "sales-by-day": "report.sales_by_day.view",
  "cost-margin": "report.cost_margin.view",
  // TAX2-S2 (DEC-312): the worksheet is its own permission.
  "sales-tax": "report.sales_tax.view",
  // ABCRPT1: its own permission.
  "abc-spirits": "report.abc_spirits.view",
  // RPT2: its own permission; reading the report and saving the location's target both need it.
  "menu-profitability": "report.menu_profitability.view",
  // PAY2-S2: the location's own royalty statement rides the Sales by day permission (no new permission).
  "royalty-statement": "report.sales_by_day.view",
  // CRB1: opening the builder is its own permission; running a report needs report.custom.build on top (customReport.ts).
  custom: "report.custom.view",
};

/** UX102: the one-sentence blurb under each report title is gone (it repeated what the title and the figures say);
 * only Sales tax keeps its line, because that one states what the worksheet does not do. */
const REPORT_TITLES: Record<ReportId, { title: string; sub: string }> = {
  "sales-by-day": {
    title: "Sales by day",
    sub: "",
  },
  "cost-margin": {
    title: "Cost and margin",
    sub: "",
  },
  "abc-spirits": {
    title: "Spirits used vs sold (ABC)",
    sub: "",
  },
  "sales-tax": {
    title: "Sales tax",
    sub: WORKSHEET_NEVER_FILES,
  },
  "menu-profitability": {
    title: "Menu profitability",
    sub: "",
  },
  "royalty-statement": {
    title: "Royalty",
    sub: "",
  },
  custom: {
    title: "Custom report",
    sub: "",
  },
};

/** The chip each report opens on. */
function defaultPresetFor(report: ReportId | undefined): RangePreset {
  if (report === "menu-profitability") return MP_DEFAULT_PRESET;
  return isInventoryRange(report) ? "lastWeek" : "last7";
}

interface ReportsState {
  /** The session whose figures are on screen; a different one resets all. */
  session: Session | undefined;
  locations: PlanningLocationDto[];
  locationId: string | undefined;
  report: ReportId | undefined;
  preset: RangePreset;
  from: string;
  to: string;
  context: ReportContextDto | undefined;
  contextLocationId: string | undefined;
  loadedAt: number | undefined;
  /** Bumped by every reset and every load, so a response that lands after a
   * sign-out, a report switch or a newer request discards itself. */
  generation: number;
  dayReport: SalesByDayReportDto | undefined;
  costReport: CostMarginReportDto | undefined;
  costFilters: CostMarginFilterState;
  costShowAll: boolean;
  costShowAllDays: boolean;
  costShowAllLists: boolean;
  costOpen: Set<string>;
  costTie: boolean;
  /** TAX2-S2, TAX2-S2-F1: the period on screen (undefined: the server opens on the location's saved filing frequency) and the
   * basis (undefined: the location's saved one). */
  taxReport: SalesTaxReportDto | undefined;
  /** ABCRPT1: the month on screen (undefined: the server opens on the last full month) and its report. */
  abcReport: AbcSpiritsReportDto | undefined;
  abcMonth: string | undefined;
  taxPeriod: PeriodSelection | undefined;
  taxBasis: WorksheetBasis | undefined;
  /** A save of a default is under way, or failed. */
  taxSaving: boolean;
  taxSaveFailed: boolean;
  /** PAY2-S2: the royalty statement on screen, the month chosen and the server's default month the picker is built around. */
  rsReport: RoyaltyStatementDto | null | undefined;
  rsMonth: string | undefined;
  rsDefaultMonth: string | undefined;
  /** RPT2: the Menu profitability report on screen and the choices over it (quadrant filter, sort, open cards). */
  mpReport: MenuProfitabilityReportDto | undefined;
  mpView: MenuProfitabilityView;
}

function freshMpView(): MenuProfitabilityView {
  return { quadrant: null, sort: "totalMargin", open: new Set<string>(), saving: false, saveFailed: false };
}

function freshCostFilters(): CostMarginFilterState {
  return {
    category: "",
    onlyBelow: false,
    onlyNoCost: false,
    onlyPartial: false,
    onlyRemade: false,
    onlyDiscounts: false,
    sort: "worst",
  };
}

function freshState(): ReportsState {
  return {
    session: undefined,
    locations: [],
    locationId: undefined,
    report: undefined,
    preset: "last7",
    from: "",
    to: "",
    context: undefined,
    contextLocationId: undefined,
    loadedAt: undefined,
    generation: 0,
    dayReport: undefined,
    costReport: undefined,
    costFilters: freshCostFilters(),
    costShowAll: false,
    costShowAllDays: false,
    costShowAllLists: false,
    costOpen: new Set<string>(),
    costTie: true,
    taxReport: undefined,
    abcReport: undefined,
    abcMonth: undefined,
    taxPeriod: undefined,
    taxSaving: false,
    taxSaveFailed: false,
    rsReport: undefined,
    rsMonth: undefined,
    rsDefaultMonth: undefined,
    taxBasis: undefined,
    mpReport: undefined,
    mpView: freshMpView(),
  };
}

let state: ReportsState = freshState();

// ---------------------------------------------------------------------
// Tiny DOM builder (textContent only: report names are user-entered)
// ---------------------------------------------------------------------

interface Opts {
  cls?: string;
  text?: string;
  testid?: string;
  attrs?: Record<string, string>;
  /** CSS properties set through the CSSOM (never a style attribute string). */
  style?: Record<string, string>;
}

function h(tag: string, opts: Opts = {}, children: Array<Node | string> = []): HTMLElement {
  const element = document.createElement(tag);
  if (opts.cls !== undefined) element.className = opts.cls;
  if (opts.text !== undefined) element.textContent = opts.text;
  if (opts.testid !== undefined) element.dataset["testid"] = opts.testid;
  for (const [key, value] of Object.entries(opts.attrs ?? {})) {
    element.setAttribute(key, value);
  }
  for (const [key, value] of Object.entries(opts.style ?? {})) {
    element.style.setProperty(key, value);
  }
  for (const child of children) {
    element.append(child);
  }
  return element;
}

function clear(element: HTMLElement): void {
  while (element.firstChild !== null) {
    element.removeChild(element.firstChild);
  }
}

function moneyTd(cell: MoneyCell, extraCls = ""): HTMLElement {
  const cls = [cell.tone === "zero" ? "zero" : "", cell.tone === "reduction" ? "neg" : "", extraCls]
    .filter((c) => c !== "")
    .join(" ");
  return h("td", { cls, text: cell.text });
}

// ---------------------------------------------------------------------
// Entering, leaving, and the header controls
// ---------------------------------------------------------------------

/** The reports this session may open, in the rail's own order. */
function availableReports(session: Session): ReportId[] {
  const all: ReportId[] = [
    "sales-by-day",
    "cost-margin",
    "abc-spirits",
    "sales-tax",
    "menu-profitability",
    "royalty-statement",
    // Last, so a person who can open any other report still lands on Sales by day.
    "custom",
  ];
  return all.filter((id) => {
    const permission = REPORT_PERMISSION[id];
    return permission === undefined || hasPermission(session, permission);
  });
}

function applyNavVisibility(session: Session): void {
  const available = new Set(availableReports(session));
  for (const [id, button] of Object.entries(NAV_BUTTONS) as Array<[ReportId, HTMLButtonElement]>) {
    button.hidden = !available.has(id);
  }
  sharedReportNavigation.refresh(state.report);
}

/**
 * Called by admin.ts each time the Reports view is shown (UX80: from the account menu). A different
 * session than last time resets everything first; the same session keeps its
 * chosen report and range but always reloads, so what is on screen is never
 * older than the owner's last visit.
 */
export async function openReports(
  session: Session,
  preferred?: ReportId,
  restoredRange?: ReportRange,
  openCanned?: string,
): Promise<void> {
  // RPTLAYOUT1-F1-S2: an old address for a retired view names the canned report that replaced it; the home opens on it.
  pendingCannedOpen = openCanned;
  if (state.session !== undefined && state.session.userId !== session.userId) {
    resetReports();
  }
  state.session = session;
  const locationResult = await getReportLocations(session.apiBaseUrl, session.tenantId);
  if (locationResult.status === 200) {
    state.locations = locationResult.body.locations;
    if (!state.locations.some((siteLocation) => siteLocation.id === state.locationId)) {
      state.locationId = state.locations[0]?.id;
    }
  } else {
    state.locations = [];
    state.locationId = undefined;
  }
  // UX108: a range from the address, applied once the report's own chips are known (applyRestoredRange).
  pendingRestoredRange = restoredRange;
  applyNavVisibility(session);
  const available = availableReports(session);
  // UX80: the account menu names the report it opened ("Cost and margin"), so
  // that report shows; with none named, the last one chosen stays.
  if (preferred !== undefined && available.includes(preferred)) {
    state.report = preferred;
  }
  if (state.report === undefined || !available.includes(state.report)) {
    state.report = available[0];
  }
  if (state.report === undefined) {
    if(preferred===undefined){await openCannedCatalog();return;}
    viewEl.hidden = true;
    hintEl.hidden = false;
    return;
  }
  if(preferred===undefined){await openCannedCatalog();return;}
  await showReport(state.report);
}

/** The canned report the Reports home opens on load, from an old address (consumed by openCannedCatalog). */
let pendingCannedOpen: string | undefined;
/** RPTMINE1: a template the home asked for; the template screen (customReport.ts) opens and runs it. */
let pendingTemplateId: string | undefined;

/** UX108: a range read from the address, waiting for the business date it is computed against. */
let pendingRestoredRange: ReportRange | undefined;

/** UX108: what the address says about the report on screen. The range is left out for a report with no date range
 * (Stock on hand, Sales tax). */
export function reportsRouteState(): { report?: ReportId; range?: ReportRange } {
  // RPTLAYOUT1-F1-S2: the Reports home and the canned results behind it are the address "#/view/reports"; the report the rail last
  // had open must not turn a refresh of the home into a refresh of an old view.
  if (cannedVisible) return {};
  const report = state.report;
  if (report === undefined) return {};
  if (report === "sales-tax" || report === "abc-spirits" || report === "royalty-statement" || report === "custom") return { report };
  if (state.from === "" || state.to === "") return { report };
  const known = (ROUTE_RANGE_PRESETS as readonly string[]).includes(state.preset);
  if (!known) return { report };
  return {
    report,
    range:
      state.preset === "custom"
        ? { preset: "custom", from: state.from, to: state.to }
        : { preset: state.preset },
  };
}

/** UX108: puts a range read from the address in place of the report's default one, when this report offers that
 * chip and the dates are usable. Anything else leaves the default. Consumed once. */
function applyRestoredRange(): void {
  const wanted = pendingRestoredRange;
  pendingRestoredRange = undefined;
  if (wanted === undefined || !presetsForReport().some((p) => p.id === wanted.preset)) return;
  const today = todayForReport();
  if (wanted.preset === "custom") {
    if (wanted.from === undefined || wanted.to === undefined) return;
    if (today !== undefined && wanted.to > today) return;
    if (inclusiveDayCount(wanted.from, wanted.to) > 92) return;
    state.preset = "custom";
    state.from = wanted.from;
    state.to = wanted.to;
    return;
  }
  if (today === undefined) return;
  const range = rangeForPreset(wanted.preset, today);
  if (range === null) return;
  state.preset = wanted.preset;
  state.from = range.from;
  state.to = range.to;
}

/**
 * A permission snapshot refreshed while the group is already on screen
 * (admin.ts's refreshTabVisibility): re-apply what to OFFER without reloading
 * the figures, and leave a report the session may no longer open.
 */
export function refreshReportsGating(session: Session): void {
  if(cannedVisible){state.session=session;void openCannedCatalog();return;}
  if (state.session === undefined) return;
  state.session = session;
  applyNavVisibility(session);
  downloadButton.hidden =
    state.report === undefined ||
    state.report === "menu-profitability" ||
    !hasPermission(session, "report.export") ||
    (state.report === "custom" && !mayExportCustom(session));
  if (state.report !== undefined && !availableReports(session).includes(state.report)) {
    void openReports(session);
  }
}

/** Wipes every figure and every choice off the screen (admin.ts invalidate()). */
export function resetReports(): void {
  pendingCannedOpen = undefined;
  pendingTemplateId = undefined;
  cannedFrame?.destroy();cannedFrame=undefined;cannedVisible=false;cannedHost.hidden=true;cannedHost.replaceChildren();sharedReportNavigation.element.hidden=false;
  const nextGeneration = state.generation + 1;
  state = freshState();
  state.generation = nextGeneration;
  viewEl.hidden = true;
  hintEl.hidden = false;
  clear(bodyEl);
  clear(freshEl);
  clear(rangeChipsEl);
  statusEl.hidden = true;
  clear(statusEl);
  titleEl.textContent = "";
  subEl.textContent = "";
  rangeSpanEl.textContent = "";
  downloadStatusEl.textContent = "";
  resetCustomReport();
  leaveReports();
  customRowEl.hidden = true;
  customErrorEl.textContent = "";
  customFromInput.value = "";
  customToInput.value = "";
  downloadButton.hidden = true;
  downloadButton.textContent = "Download CSV";
  exportMenu.close();
  rangebarEl.hidden = false;
  for (const button of Object.values(NAV_BUTTONS)) {
    button.classList.remove("on");
    button.setAttribute("aria-pressed", "false");
  }
}

async function showReport(report: ReportId): Promise<void> {
  // UIFIX1 (SF-6): the pill row and the operations header's Menu button give way to the report's own back link.
  setMenuBackHidden(true);
  cannedFrame?.destroy();cannedFrame=undefined;cannedVisible=false;cannedHost.hidden=true;sharedReportNavigation.element.hidden=true;
  hideHoverHelp();
  const session = state.session;
  if (session === undefined) return;
  state.report = report;
  // VER89: choosing another report retires any load still in flight for the one before, even when the two share a location
  // and a business date and so need no new context fetch (Sales by day then Sales by item); otherwise the older report
  // finishes later and draws over the one just chosen.
  state.generation += 1;
  requestRoutePublish();
  if (report !== "cost-margin" || state.costReport === undefined) {
    state.costFilters = freshCostFilters();
    state.costShowAll = false;
    state.costShowAllDays = false;
    state.costShowAllLists = false;
    state.costOpen = new Set<string>();
    state.costTie = true;
  }
  if (report !== "menu-profitability" || state.mpReport === undefined) {
    state.mpView = freshMpView();
  }
  applyNavVisibility(session);
  hintEl.hidden = true;
  viewEl.hidden = false;
  const copy = REPORT_TITLES[report];
  titleEl.textContent = copy.title;
  subEl.textContent = copy.sub;
  subEl.hidden = copy.sub === "";
  const locationReport =
    report === "sales-by-day" || report === "sales-tax" || report === "abc-spirits" || report === "royalty-statement" ||
    isInventoryRange(report);
  locationWrapEl.hidden = !locationReport || state.locations.length === 0;
  if (locationReport) {
    locationSelectEl.replaceChildren();
    for (const siteLocation of state.locations) {
      const option = document.createElement("option");
      option.value = siteLocation.id;
      option.textContent = siteLocation.name;
      locationSelectEl.append(option);
    }
    locationSelectEl.value = state.locationId ?? "";
  }
  // The custom report shows Export only once a report has run (customReport.ts calls back through onRan).
  downloadButton.hidden =
    !hasPermission(session, "report.export") ||
    (report === "custom" && !mayExportCustom(session));
  downloadButton.textContent = downloadLabelFor(report);
  exportMenu.close();
  downloadStatusEl.textContent = "";
  // The Sales tax worksheet has its own month and basis controls inside its body (TAX2-S2).
  // The custom report builder has its own dates, locations and run inside its body (CRB1).
  rangebarEl.hidden =
    report === "sales-tax" || report === "abc-spirits" || report === "royalty-statement" || report === "custom";
  // Its own strip is drawn inside the body, so the shared freshness strip must not show empty.
  freshEl.hidden = report === "sales-tax" || report === "abc-spirits" || report === "royalty-statement" || report === "custom";
  customRowEl.hidden = true;
  if (report === "sales-tax" || report === "abc-spirits" || report === "royalty-statement" || report === "custom") {
    await loadReport();
    return;
  }
  await ensureContextAndRange();
  // UX108: a range that came with the address wins over the default, unless the report was already showing one
  // this visit (then the context was known and ensureContextAndRange returned early).
  applyRestoredRange();
  // A chip the other report offers (Last 7 days, Today, Last month) may not
  // exist here: fall back to this report's own default and recompute the range.
  if (!presetsForReport().some((p) => p.id === state.preset)) {
    state.preset = defaultPresetFor(report);
    const today = todayForReport();
    const range = today === undefined ? null : rangeForPreset(state.preset, today);
    if (range !== null) {
      state.from = range.from;
      state.to = range.to;
    }
  }
  renderRangeChips();
  await loadReport();
}

/** The inventory reports (Cost and margin, Menu profitability) share one set of chips,
 * one business night and the 92-day limit (DEC-290, DEC-291). */
function isInventoryRange(report: ReportId | undefined): boolean {
  return (
    report === "cost-margin" ||
    // RPT2: one location's drinks over the business night, the same 92-day limit.
    report === "menu-profitability"
  );
}

/** The chips this report offers: the inventory report's own list (DEC-290). */
function presetsForReport(): ReadonlyArray<{ id: RangePreset; label: string }> {
  if (state.report === "menu-profitability") return MP_PRESETS;
  return isInventoryRange(state.report) ? USAGE_WASTE_PRESETS : RANGE_PRESETS;
}

/** The business date "today" means for the report on screen. The inventory
 * reports use the business night by the clock (DEC-290), which only differs
 * from the sale stamp after a closeout pushed it forward. */
function todayForReport(): string | undefined {
  const context = state.context;
  if (context === undefined) return undefined;
  return isInventoryRange(state.report) ? (context.clockToday ?? context.today) : context.today;
}

async function ensureContextAndRange(): Promise<void> {
  const session = state.session;
  if (session === undefined) return;
  const locationId = state.report === "sales-by-day" || isInventoryRange(state.report)
    ? state.locationId : undefined;
  if (state.context !== undefined && state.contextLocationId === locationId && state.from !== "") return;
  if (state.contextLocationId !== locationId) {
    state.context = undefined;
    state.from = "";
    state.to = "";
  }
  state.generation += 1;
  const gen = state.generation;
  const result = await getReportContext(session.apiBaseUrl, session.tenantId, locationId);
  if (gen !== state.generation) return;
  if (result.status !== 200 || typeof result.body.today !== "string") {
    showError(
      "Could not load the business date.",
      result.body.message,
      () => void ensureContextAndRange().then(() => loadReport()),
    );
    return;
  }
  state.context = {
    today: result.body.today,
    timezone: result.body.timezone,
    cutoffLocal: result.body.cutoffLocal,
    ...(result.body.clockToday === undefined ? {} : { clockToday: result.body.clockToday }),
  };
  state.contextLocationId = locationId;
  state.preset = isInventoryRange(state.report) ? defaultPresetFor(state.report) : state.preset;
  const range = rangeForPreset(state.preset, todayForReport() ?? state.context.today);
  if (range !== null) {
    state.from = range.from;
    state.to = range.to;
  }
}

function renderRangeChips(): void {
  clear(rangeChipsEl);
  for (const preset of presetsForReport()) {
    const chip = h("button", {
      cls: `chip${state.preset === preset.id ? " on" : ""}`,
      text: preset.label,
      testid: `reports-range-${preset.id}`,
      attrs: { type: "button", "aria-pressed": String(state.preset === preset.id) },
    });
    chip.addEventListener("click", () => handlePresetClick(preset.id));
    rangeChipsEl.append(chip);
  }
  customRowEl.hidden = state.preset !== "custom";
  if (state.preset === "custom") {
    customFromInput.value = state.from;
    customToInput.value = state.to;
    const latest = todayForReport();
    if (isInventoryRange(state.report) && latest !== undefined) {
      // The pickers never offer a day that has not happened (DEC-290).
      customFromInput.max = latest;
      customToInput.max = latest;
    } else {
      customFromInput.removeAttribute("max");
      customToInput.removeAttribute("max");
    }
  }
  if (state.from !== "" && state.to !== "") {
    const days = inclusiveDayCount(state.from, state.to);
    clear(rangeSpanEl);
    rangeSpanEl.append(
      h("b", { text: dayLabel(state.from) }),
      " to ",
      h("b", { text: dayLabel(state.to) }),
      `, ${days} business ${days === 1 ? "day" : "days"}`,
    );
  }
  // UX108: the chosen range is part of the address.
  requestRoutePublish();
}

function handlePresetClick(preset: RangePreset): void {
  state.preset = preset;
  customErrorEl.textContent = "";
  const today = todayForReport();
  if (preset === "custom") {
    // Open the two date pickers on the range already on screen.
    renderRangeChips();
    return;
  }
  if (today === undefined) return;
  const range = rangeForPreset(preset, today);
  if (range === null) return;
  state.from = range.from;
  state.to = range.to;
  renderRangeChips();
  void loadReport();
}

function handleCustomApply(): void {
  const from = customFromInput.value;
  const to = customToInput.value;
  const today = todayForReport();
  customErrorEl.textContent = "";
  if (from === "" || to === "") {
    customErrorEl.textContent = "Pick both a start date and an end date.";
    return;
  }
  if (from > to) {
    customErrorEl.textContent = "The start date is after the end date.";
    return;
  }
  if (today !== undefined && to > today) {
    customErrorEl.textContent = `The latest day you can pick is ${dayLabel(today)}.`;
    return;
  }
  if (inclusiveDayCount(from, to) > 92) {
    customErrorEl.textContent = "A report covers at most 92 days at a time.";
    return;
  }
  state.from = from;
  state.to = to;
  renderRangeChips();
  void loadReport();
}

// ---------------------------------------------------------------------
// Loading
// ---------------------------------------------------------------------

function showError(lead: string, serverMessage: string | undefined, retry: () => void): void {
  clear(statusEl);
  statusEl.hidden = false;
  statusEl.dataset["testid"] = "reports-error";
  statusEl.className = "reports-status reports-status-error";
  const text =
    serverMessage !== undefined && serverMessage !== ""
      ? `${lead} ${capitalizeServerMessage(serverMessage)}`
      : lead;
  statusEl.append(h("p", { text }));
  const again = h("button", {
    cls: "btn-secondary btn-compact",
    text: "Try again",
    testid: "reports-retry",
    attrs: { type: "button" },
  });
  again.addEventListener("click", retry);
  statusEl.append(again);
  clear(bodyEl);
}

function showLoading(): void {
  clear(statusEl);
  statusEl.hidden = false;
  statusEl.dataset["testid"] = "reports-loading";
  statusEl.className = "reports-status";
  statusEl.append(h("p", { text: "Loading." }));
}

function hideStatus(): void {
  clear(statusEl);
  statusEl.hidden = true;
}

// ---------------------------------------------------------------------
// Custom report (CRB1): the body is drawn by ui/src/customReport.ts; the Export control follows what ran.
// ---------------------------------------------------------------------

/** RPTLAYOUT1-F1-S2: the operations header's Menu button returns a report to the Reports home. On the home itself there is
 * nowhere to return to (the old hub is gone), so the button is not drawn; the top bar's Home button leaves Reports. */
function setMenuBackHidden(hidden: boolean): void {
  const button = document.getElementById("admin-ops-back");
  if (button !== null) button.hidden = hidden;
}
/** Exported for admin.ts: leaving the Reports view for any other screen brings the Menu button back.
 * DEC-500 (UIFIX1): Reports use the theme of the console they sit in. The location app no longer switches to a light
 * Reports theme, so there is no theme to put back. */
export function leaveReports(): void {
  setMenuBackHidden(false);
}

async function loadCustomReport(session: Session): Promise<void> {
  // RPTLAYOUT1-F1-S2: the old saved list, versions and archive screens are retired with the Custom rail entry; saved reports are
  // cards on the Reports home. Only a template the home asked for still opens through this module.
  const templateId = pendingTemplateId;
  pendingTemplateId = undefined;
  if (templateId === undefined) {
    await openCannedCatalog();
    return;
  }
  const gen = state.generation + 1;
  state.generation = gen;
  hideStatus();
  clear(bodyEl);
  clear(freshEl);
  await openCustomReport(bodyEl, session, {
    onRan: () => {
      if (gen !== state.generation && state.report !== "custom") return;
      downloadButton.hidden = !hasPermission(session, "report.export") || !mayExportCustom(session);
    },
    openBuilder: (editing) => void openCannedCatalog({ kind: "builder", ...(editing === undefined ? {} : { editing }) }),
    templateId,
    back: () => void openCannedCatalog(),
    openResults: (id) => void openCannedCatalog({ kind: "results", id }),
  });
}

/** Export shows once a report has run: an unsaved one needs report.custom.build, a saved one report.custom.view, both with report.export. */
function mayExportCustom(session: Session): boolean {
  const target = customReportExportTarget();
  return mayExport((permission) => hasPermission(session, permission), target !== undefined, target?.kind === "saved");
}

async function handleCustomDownload(session: Session, format: GateExportFormat): Promise<void> {
  const target = customReportExportTarget();
  if (target === undefined) return;
  downloadStatusEl.textContent = "";
  downloadButton.disabled = true;
  try {
    const result =
      target.kind === "saved"
        ? await exportSavedReport(session.apiBaseUrl, session.tenantId, target.id, target.version, format)
        : await exportCustomReport(session.apiBaseUrl, session.tenantId, target.spec, format);
    if (result.status !== 200 || result.blob === undefined) {
      downloadStatusEl.textContent = result.networkError
        ? "Could not download this file. Check your connection."
        : capitalizeServerMessage(result.message ?? "Could not download this file.");
      return;
    }
    await deliverExportFile(downloadStatusEl, result.blob, "custom-report", format);
  } finally {
    downloadButton.disabled = false;
  }
}

async function loadReport(): Promise<void> {
  const session = state.session;
  const report = state.report;
  if (session === undefined || report === undefined) {
    return;
  }
  if (report === "sales-tax") {
    await loadSalesTax(session);
    return;
  }
  if (report === "abc-spirits") {
    await loadAbcSpirits(session);
    return;
  }
  if (report === "royalty-statement") {
    await loadRoyaltyStatement(session);
    return;
  }
  if (report === "custom") {
    await loadCustomReport(session);
    return;
  }
  if (state.from === "" || state.to === "") {
    return;
  }
  state.generation += 1;
  const gen = state.generation;
  const { from, to } = state;
  showLoading();
  clear(bodyEl);

  if (report === "sales-by-day") {
    const result = await getSalesByDayReport(session.apiBaseUrl, session.tenantId, from, to, state.locationId);
    if (gen !== state.generation) return;
    if (result.status !== 200 || !Array.isArray(result.body.days)) {
      showError("Could not load Sales by day.", result.body.message, () => void loadReport());
      return;
    }
    state.dayReport = result.body;
    state.loadedAt = Date.now();
    hideStatus();
    renderFreshness(result.body.meta);
    // DSC21: discount cost by kind for the same location and dates, drawn under the day table. A location that may not read it
    // (or a failed read) simply has no card; the day report never waits on it.
    const discounts = await getDiscountKindReport(session.apiBaseUrl, session.tenantId, from, to, state.locationId);
    if (gen !== state.generation) return;
    if (discounts.status !== 200 && discounts.status !== 403) {
      console.warn(`DSC21: the discount report did not load (${discounts.status}); the card is left out.`);
    }
    renderSalesByDay(result.body, discounts.status === 200 && Array.isArray(discounts.body.rows) ? discounts.body : undefined);
  } else if (report === "cost-margin") {
    const result = await getCostMarginReport(
      session.apiBaseUrl,
      session.tenantId,
      from,
      to,
      costQuery(),
    );
    if (gen !== state.generation) return;
    if (result.status === 403) {
      showNoCostPermission();
      return;
    }
    if (result.status !== 200 || !Array.isArray(result.body.drinks)) {
      showError("Could not load Cost and margin.", result.body.message, () => void loadReport());
      return;
    }
    state.costReport = result.body;
    state.loadedAt = Date.now();
    hideStatus();
    renderFreshnessInventory(result.body.meta);
    renderCost(result.body);
    downloadButton.textContent = `Export (${result.body.drinks.length} ${
      result.body.drinks.length === 1 ? "drink" : "drinks"
    })`;
    downloadButton.hidden =
      !hasPermission(session, "report.export") || result.body.drinks.length === 0;
  } else if (report === "menu-profitability") {
    const result = await getMenuProfitabilityReport(
      session.apiBaseUrl,
      session.tenantId,
      from,
      to,
      state.locationId,
    );
    if (gen !== state.generation) return;
    if (result.status !== 200 || !Array.isArray(result.body.drinks)) {
      showError("Could not load Menu profitability.", result.body.message, () => void loadReport());
      return;
    }
    state.mpReport = result.body;
    state.loadedAt = Date.now();
    hideStatus();
    renderFreshnessInventory(result.body.meta);
    renderMenuProfit(result.body);
    downloadButton.hidden =
      !hasPermission(session, "report.export") || result.body.drinks.length === 0;
  }
}

// ---------------------------------------------------------------------
// Sales tax worksheet (TAX2-S2, DEC-304): the body is drawn by ui/src/salesTaxWorksheet.ts
// ---------------------------------------------------------------------

async function loadSalesTax(session: Session): Promise<void> {
  state.generation += 1;
  const gen = state.generation;
  showLoading();
  clear(bodyEl);
  clear(freshEl);
  const result = await getSalesTaxWorksheetReport(session.apiBaseUrl, session.tenantId, {
    ...(state.taxPeriod === undefined ? {} : { period: periodQuery(state.taxPeriod) }),
    ...(state.taxBasis === undefined ? {} : { basis: state.taxBasis }),
    ...(state.locationId === undefined ? {} : { locationId: state.locationId }),
  });
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.body.worksheet === undefined) {
    const refusal = describeWorksheetRefusal(result.status, result.body.error, result.body.message);
    downloadButton.hidden = true;
    if (refusal.kind === "failed") {
      showError(refusal.title, refusal.body, () => void loadReport());
      return;
    }
    hideStatus();
    clear(bodyEl);
    bodyEl.append(
      h("div", { cls: "card", testid: `stw-refusal-${refusal.kind}` }, [
        h("div", { cls: "empty" }, [
          h("h3", { text: refusal.title }),
          h("p", { text: refusal.body }),
          ...(refusal.nextStep === undefined
            ? []
            : [h("p", { text: refusal.nextStep, testid: "stw-next-step" })]),
        ]),
      ]),
    );
    return;
  }
  state.taxReport = result.body;
  state.loadedAt = Date.now();
  // What the server answered is the truth about the period and basis on screen, including the saved ones it opened on.
  state.taxPeriod = selectionFromReport(result.body);
  state.taxBasis = result.body.worksheet.basis;
  hideStatus();
  downloadButton.hidden = !hasPermission(session, "report.export");
  renderSalesTax(result.body);
}

// ---------------------------------------------------------------------
// Spirits used vs sold (ABC) (ABCRPT1): the body is drawn by ui/src/abcSpirits.ts
// ---------------------------------------------------------------------

async function loadAbcSpirits(session: Session): Promise<void> {
  state.generation += 1;
  const gen = state.generation;
  showLoading();
  clear(bodyEl);
  clear(freshEl);
  const result = await getAbcSpiritsReport(session.apiBaseUrl, session.tenantId, {
    ...(state.abcMonth === undefined ? {} : { month: state.abcMonth }),
    ...(state.locationId === undefined ? {} : { locationId: state.locationId }),
  });
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.body.rows === undefined) {
    downloadButton.hidden = true;
    // A refusal that names what to fix (the location has no state, or is not in a state this report covers) is shown as the
    // page's own sentence, not as a failed load.
    if (result.status === 400 && result.body.message !== undefined) {
      hideStatus();
      bodyEl.append(
        h("div", { cls: "card", testid: "abc-refusal" }, [
          h("div", { cls: "empty" }, [
            h("h3", { text: "This report is not available here yet" }),
            h("p", { text: capitalizeServerMessage(result.body.message) }),
          ]),
        ]),
      );
      return;
    }
    showError("Could not load Spirits used vs sold.", result.body.message, () => void loadReport());
    return;
  }
  state.abcReport = result.body;
  state.abcMonth = result.body.meta.month;
  state.loadedAt = Date.now();
  hideStatus();
  downloadButton.hidden = !hasPermission(session, "report.export");
  renderAbcSpirits(bodyEl, result.body, {
    onPrevious: () => {
      state.abcMonth = shiftMonth(result.body.meta.month, -1);
      void loadReport();
    },
    onNext: () => {
      state.abcMonth = shiftMonth(result.body.meta.month, 1);
      void loadReport();
    },
  });
}

async function handleAbcDownload(session: Session, format: GateExportFormat, sheet?: string): Promise<void> {
  const month = state.abcMonth;
  if (month === undefined) return;
  const gen = state.generation;
  downloadStatusEl.textContent = "";
  const result = await getAbcSpiritsFile(
    session.apiBaseUrl,
    session.tenantId,
    month,
    state.locationId,
    format,
    sheet === "size" || sheet === "figures" ? sheet : undefined,
  );
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.blob === undefined) {
    downloadStatusEl.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(
    downloadStatusEl,
    result.blob,
    `spirits-used-vs-sold${format === "csv" && (sheet === "size" || sheet === "figures") ? `-${sheet}` : ""}_${month}`,
    format,
  );
}

function renderSalesTax(report: SalesTaxReportDto): void {
  const session = state.session;
  const selection = state.taxPeriod ?? selectionFromReport(report);
  const reload = (next: PeriodSelection): void => {
    state.taxPeriod = next;
    void loadReport();
  };
  renderSalesTaxWorksheet(
    bodyEl,
    report,
    {
      loadedAt: state.loadedAt ?? Date.now(),
      // Saving a default is the owner function the tax rate uses; a holder of the view right alone is never offered it.
      canSaveDefaults: session !== undefined && hasPermission(session, "admin.tax_rate.manage"),
      saving: state.taxSaving,
      saveFailed: state.taxSaveFailed,
    },
    {
      onPrevious: () => reload(shiftSelection(selection, -1)),
      onNext: () => reload(shiftSelection(selection, 1)),
      onKind: (kind: PeriodKind) => reload(switchKind(selection, kind)),
      onCustom: (from: string, to: string) => reload(customSelection(from, to)),
      onBasis: (basis) => {
        state.taxBasis = basis;
        void loadReport();
      },
      onSaveDefault: (which) => void saveSalesTaxDefault(which, selection),
    },
  );
}

/** TAX2-S2-F1: saves the period kind or the date basis on screen as the location's default, then redraws with the saved value. */
async function saveSalesTaxDefault(which: "period" | "basis", selection: PeriodSelection): Promise<void> {
  const session = state.session;
  const report = state.taxReport;
  if (session === undefined || report === undefined || state.taxSaving) return;
  const frequency = kindFilingFrequency(selection.kind);
  const input =
    which === "period"
      ? frequency === null
        ? undefined
        : { filingFrequency: frequency }
      : { dateBasis: report.worksheet.basis };
  if (input === undefined) return;
  const gen = state.generation;
  state.taxSaving = true;
  state.taxSaveFailed = false;
  renderSalesTax(report);
  const result = await putSalesTaxFilingSettings(session.apiBaseUrl, session.tenantId, {
    ...input,
    ...(state.locationId === undefined ? {} : { locationId: state.locationId }),
  });
  if (gen !== state.generation) return;
  state.taxSaving = false;
  const saved = result.status === 200 ? result.body.settings : undefined;
  if (saved === undefined) {
    state.taxSaveFailed = true;
  } else {
    state.taxReport = {
      ...report,
      context: {
        ...report.context,
        filing: { frequency: saved.frequency, dateBasis: saved.dateBasis },
      },
    };
  }
  renderSalesTax(state.taxReport ?? report);
}

// ---------------------------------------------------------------------
// Royalty (PAY2-S2, PAY2-S8, PAY2-S9): the statement and the charges of one month, drawn as one row by
// ui/src/locationRoyaltyPanel.ts
// ---------------------------------------------------------------------

async function loadRoyaltyStatement(session: Session): Promise<void> {
  state.generation += 1;
  const gen = state.generation;
  showLoading();
  clear(bodyEl);
  clear(freshEl);
  const result = await getRoyaltyStatement(session.apiBaseUrl, session.tenantId, {
    ...(state.rsMonth === undefined ? {} : { month: state.rsMonth }),
    ...(state.locationId === undefined ? {} : { locationId: state.locationId }),
  });
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.body.statement === undefined) {
    showError("Could not load the Royalty.", result.body.message, () => void loadReport());
    return;
  }
  const statement = result.body.statement;
  state.rsReport = statement;
  if (statement !== null) {
    if (state.rsMonth === undefined) state.rsDefaultMonth = statement.month;
    state.rsMonth = statement.month;
  }
  // This location's charges, adjustments and disputes. A location that is not a franchise location answers null.
  let ledger: LocationLedgerDto | null = null;
  let ledgerFailed = false;
  if (statement !== null) {
    const reply = await getRoyaltyCharges(session.apiBaseUrl, session.tenantId, state.locationId);
    if (gen !== state.generation) return;
    if (reply.status !== 200) ledgerFailed = true;
    else if (isLedgerShape(reply.body.ledger)) ledger = reply.body.ledger;
    else if (reply.body.ledger !== null) ledgerFailed = true;
  }
  state.loadedAt = Date.now();
  hideStatus();
  // The one Export sits in the royalty view itself; the page's own Download button is not used for this report.
  downloadButton.hidden = true;
  renderLocationRoyalty(
    bodyEl,
    statement === null
      ? null
      : { statement, ledger, ledgerFailed, defaultMonth: state.rsDefaultMonth ?? statement.month },
    {
      onMonth: (month) => {
        state.rsMonth = month;
        void loadReport();
      },
      reload: () => void loadReport(),
      googleSession: () => ({ apiBaseUrl: session.apiBaseUrl, tenantId: session.tenantId }),
      fileDispute: async (chargeId, body) => {
        const reply = await postRoyaltyDispute(session.apiBaseUrl, session.tenantId, chargeId, body);
        return reply.status === 201
          ? { ok: true as const }
          : { ok: false as const, message: capitalizeServerMessage(reply.body.message ?? "") };
      },
      download: async (format, sheet, month, status) => {
        const file = await getRoyaltyChargesFile(
          session.apiBaseUrl,
          session.tenantId,
          format,
          sheet,
          state.locationId,
          month,
        );
        if (file.status !== 200 || file.blob === undefined) {
          status.textContent = file.networkError
            ? "Could not download this file. Check your connection."
            : capitalizeServerMessage(file.message ?? "Could not download this file.");
          return;
        }
        await deliverExportFile(
          status,
          file.blob,
          `royalty-charges${sheet === undefined ? "" : `-${sheet}`}_${month}`,
          format,
        );
      },
      canDispute: () => hasPermission(session, "admin.payments.onboard"),
      canExport: () => hasPermission(session, "report.export"),
    },
  );
}

async function handleSalesTaxDownload(session: Session, format: GateExportFormat = "csv"): Promise<void> {
  const period = state.taxPeriod;
  if (period === undefined) return;
  const gen = state.generation;
  downloadStatusEl.textContent = "";
  const result = await getSalesTaxWorksheetCsv(
    session.apiBaseUrl,
    session.tenantId,
    periodQuery(period),
    state.locationId,
    format,
  );
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.blob === undefined) {
    downloadStatusEl.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(
    downloadStatusEl,
    result.blob,
    `sales-tax-worksheet_${period.key ?? `${period.from}_${period.to}`}`,
    format,
  );
}

function costQuery(): CostMarginQuery {
  const f = state.costFilters;
  return {
    ...(state.locationId === undefined ? {} : { locationId: state.locationId }),
    ...(f.category === "" ? {} : { category: f.category }),
    sort: f.sort,
    onlyBelow: f.onlyBelow,
    onlyNoCost: f.onlyNoCost,
    onlyPartial: f.onlyPartial,
    onlyRemade: f.onlyRemade,
    onlyDiscounts: f.onlyDiscounts,
  };
}

function showNoCostPermission(): void {
  clear(statusEl);
  statusEl.hidden = true;
  clear(freshEl);
  clear(bodyEl);
  downloadButton.hidden = true;
  bodyEl.append(
    h("div", { cls: "card", testid: "cm-no-permission" }, [
      h("div", { cls: "empty" }, [
        h("h3", { text: "You do not have access to this report" }),
        h("p", {
          text: "Ask an owner or manager to turn on \u201CView the Cost and margin report\u201D for your role.",
        }),
      ]),
    ]),
  );
}

function renderCost(report: CostMarginReportDto): void {
  const session = state.session;
  renderCostMargin(
    bodyEl,
    report,
    {
      filters: state.costFilters,
      showAll: state.costShowAll,
      showAllDays: state.costShowAllDays,
      showAllLists: state.costShowAllLists,
      open: state.costOpen,
      tieOpen: state.costTie,
      canExport: session !== undefined && hasPermission(session, "report.export"),
    },
    {
      onCategory: (category) => {
        state.costFilters.category = category;
        state.costShowAll = false;
        void loadReport();
      },
      onFlag: (flag) => {
        state.costFilters[flag] = !state.costFilters[flag];
        state.costShowAll = false;
        void loadReport();
      },
      onSort: (sort) => {
        state.costFilters.sort = sort;
        void loadReport();
      },
      onClearFilters: () => {
        state.costFilters = freshCostFilters();
        void loadReport();
      },
      onShowAll: () => {
        state.costShowAll = true;
        renderCost(report);
      },
      onShowAllDays: () => {
        state.costShowAllDays = true;
        renderCost(report);
      },
      onShowAllLists: () => {
        state.costShowAllLists = true;
        renderCost(report);
      },
      onToggleOpen: (key) => {
        if (state.costOpen.has(key)) {
          state.costOpen.delete(key);
        } else {
          state.costOpen.add(key);
        }
        renderCost(report);
      },
      onToggleTie: () => {
        state.costTie = !state.costTie;
        renderCost(report);
      },
      onDownloadDays: () => void handleCostDownload("days"),
      onShowLastWeek: () => handlePresetClick("lastWeek"),
      onPickOtherDates: () => handlePresetClick("custom"),
    },
  );
}

// ---------------------------------------------------------------------
// Menu profitability (RPT2): the body is drawn by ui/src/menuProfitability.ts; a stepper tap saves the location's target
// (the server validates and audits it), then reads the report again so every price and pill comes from the server.
// ---------------------------------------------------------------------

function renderMenuProfit(report: MenuProfitabilityReportDto): void {
  renderMenuProfitability(bodyEl, report, state.mpView, {
    onQuadrant: (quadrant) => {
      state.mpView.quadrant = nextQuadrantFilter(state.mpView.quadrant, quadrant);
      renderMenuProfit(report);
    },
    onSort: (sort) => {
      state.mpView.sort = sort;
      renderMenuProfit(report);
    },
    onToggleOpen: (key) => {
      if (state.mpView.open.has(key)) state.mpView.open.delete(key);
      else state.mpView.open.add(key);
      renderMenuProfit(report);
    },
    onStep: (targetBp) => void saveMenuProfitTarget(targetBp),
  });
}

async function saveMenuProfitTarget(targetBp: number): Promise<void> {
  const session = state.session;
  const report = state.mpReport;
  if (session === undefined || report === undefined || state.mpView.saving) return;
  const gen = state.generation;
  state.mpView.saving = true;
  state.mpView.saveFailed = false;
  renderMenuProfit(report);
  const saved = await putMenuProfitabilityTarget(session.apiBaseUrl, session.tenantId, {
    targetPourCostBp: targetBp,
    ...(state.locationId === undefined ? {} : { locationId: state.locationId }),
  });
  if (gen !== state.generation) return;
  if (saved.status !== 200) {
    state.mpView.saving = false;
    state.mpView.saveFailed = true;
    renderMenuProfit(report);
    return;
  }
  const fresh = await getMenuProfitabilityReport(
    session.apiBaseUrl,
    session.tenantId,
    state.from,
    state.to,
    state.locationId,
  );
  if (gen !== state.generation) return;
  state.mpView.saving = false;
  if (fresh.status !== 200 || !Array.isArray(fresh.body.drinks)) {
    state.mpView.saveFailed = true;
    renderMenuProfit(report);
    return;
  }
  state.mpReport = fresh.body;
  state.loadedAt = Date.now();
  renderFreshnessInventory(fresh.body.meta);
  renderMenuProfit(fresh.body);
}

function renderFreshnessInventory(meta: {
  cutoffLocal: string;
  timeZone: string;
  open: boolean;
}): void {
  clear(freshEl);
  freshEl.append(
    h("span", { testid: "reports-business-day" }, [
      h("b", { text: "Business days" }),
      ` run from ${cutoffText(meta.cutoffLocal)} to ${cutoffText(meta.cutoffLocal)}, ${zoneFriendlyName(meta.timeZone)}`,
    ]),
  );
  const updated =
    state.loadedAt === undefined
      ? ""
      : new Date(state.loadedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  freshEl.append(
    h("span", { testid: "reports-updated" }, [h("b", { text: "Updated" }), ` today ${updated}`]),
  );
  if (meta.open) {
    freshEl.append(
      h("span", {
        cls: "tag warn",
        text: "Not final yet: today is still open",
        testid: "uw-not-final",
      }),
    );
  }
  freshEl.append(h("span", { cls: "spacer" }));
  const refresh = h("button", {
    cls: "btn-quiet",
    text: "Refresh",
    testid: "reports-refresh",
    attrs: { type: "button" },
  });
  refresh.addEventListener("click", () => void loadReport());
  freshEl.append(refresh);
}

// ---------------------------------------------------------------------
// Receipts and spend (INV35-B1, DEC-292): the body is drawn by ui/src/receiptsSpend.ts
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// Count variance (INV35-A1, DEC-292): the body is drawn by ui/src/countVariance.ts
// ---------------------------------------------------------------------

// ---------------------------------------------------------------------
// The shared strip: business day, updated, simulated, refresh
// ---------------------------------------------------------------------

function renderFreshness(meta: ReportMetaDto): void {
  clear(freshEl);
  freshEl.append(
    h("span", { testid: "reports-business-day" }, [
      h("b", { text: "Business day" }),
      ` ends at ${cutoffText(meta.cutoffLocal)}, ${zoneFriendlyName(meta.timezone)}`,
    ]),
  );
  const updated =
    state.loadedAt === undefined
      ? ""
      : new Date(state.loadedAt).toLocaleTimeString([], { hour: "numeric", minute: "2-digit" });
  freshEl.append(
    h("span", { testid: "reports-updated" }, [h("b", { text: "Updated" }), ` today ${updated}`]),
  );
  if (meta.paymentsSimulated) {
    freshEl.append(
      h("span", {
        cls: "tag info",
        text: "Simulated payments",
        testid: "reports-simulated-badge",
      }),
    );
  }
  freshEl.append(h("span", { cls: "spacer" }));
  const refresh = h("button", {
    cls: "btn-quiet",
    text: "Refresh",
    testid: "reports-refresh",
    attrs: { type: "button" },
  });
  refresh.addEventListener("click", () => void loadReport());
  freshEl.append(refresh);
}

// ---------------------------------------------------------------------
// Shared pieces
// ---------------------------------------------------------------------

function deltaEl(change: PercentChange | null, suffix: string): HTMLElement {
  const wrap = h("div", { cls: "d" });
  if (change === null) {
    wrap.append(h("span", { text: suffix === "" ? "" : "No earlier figure to compare" }));
    return wrap;
  }
  const arrow = change.direction === "up" ? "▲" : change.direction === "down" ? "▼" : "";
  wrap.append(
    h("span", {
      cls: `delta ${change.direction}`,
      text: `${arrow} ${change.text}`.trim(),
    }),
    ` ${suffix}`,
  );
  return wrap;
}

function kpi(
  label: string,
  value: string,
  detail: HTMLElement,
  testid: string,
  tone: "" | "warn" | "bad" = "",
): HTMLElement {
  return h("div", { cls: tone === "" ? "kpi" : `kpi ${tone}`, testid }, [
    h("div", { cls: "k", text: label }),
    h("div", { cls: "v", text: value, testid: `${testid}-value` }),
    detail,
  ]);
}

function plainDetail(text: string): HTMLElement {
  return h("div", { cls: "d", text });
}

function priorWindowText(from: string, to: string): string {
  const days = inclusiveDayCount(from, to);
  return days === 1 ? "vs the day before" : `vs the ${days} days before`;
}

function tieOutLine(
  tieOut: ReportTieOutDto,
  okText: string,
  describe: (difference: ReportTieOutDto["differences"][number]) => string,
): HTMLElement {
  if (tieOut.ok) {
    return h("span", { cls: "tie", text: okText, testid: "reports-tie-out" });
  }
  const wrap = h("span", { testid: "reports-tie-out-warning" }, [
    h("span", { cls: "tag warn", text: "Does not add up" }),
    ` ${tieOut.differences.map(describe).join(" ")}`,
  ]);
  return wrap;
}

function differenceText(
  what: string,
): (difference: ReportTieOutDto["differences"][number]) => string {
  return (difference) => {
    const where = difference.businessDate === null ? "" : `${dayLabel(difference.businessDate)}: `;
    const amount = formatMoney(Math.abs(difference.differenceCents));
    const direction = difference.differenceCents > 0 ? "more than" : "less than";
    return `${where}${what} is ${amount} ${direction} expected.`;
  };
}

function emptyCard(text: string, testid: string): HTMLElement {
  const range = state.from === "" ? "" : `${dayLabel(state.from)} to ${dayLabel(state.to)}`;
  return h("div", { cls: "card reports-empty", testid }, [
    h("p", { text }),
    h("p", { cls: "reports-empty-range", text: range }),
  ]);
}

function table(
  headers: Array<{ text: string; left?: boolean; sticky?: boolean; extra?: string }>,
  testid: string,
): { wrap: HTMLElement; tbody: HTMLElement; thead: HTMLElement } {
  const headRow = h("tr");
  for (const header of headers) {
    const cls = [
      header.left === true ? "l" : "",
      header.sticky === true ? "sticky" : "",
      header.extra ?? "",
    ]
      .filter((c) => c !== "")
      .join(" ");
    headRow.append(h("th", { cls, text: header.text }));
  }
  const thead = h("thead", {}, [headRow]);
  const tbody = h("tbody");
  const wrap = h("div", { cls: "tblwrap", testid }, [h("table", {}, [thead, tbody])]);
  return { wrap, tbody, thead };
}

// ---------------------------------------------------------------------
// Sales by day
// ---------------------------------------------------------------------

function renderSalesByDay(report: SalesByDayReportDto, discounts?: DiscountKindReportDto): void {
  clear(bodyEl);
  const hasActivity = report.days.some(
    (d) => d.tickets > 0 || d.refundCents > 0 || d.tipsCents > 0 || d.grossCents > 0,
  );
  if (!hasActivity) {
    bodyEl.append(emptyCard("No sales", "reports-empty"));
    return;
  }
  const totals = report.totals;
  const prior = report.prior;
  const windowText = prior === null ? "" : priorWindowText(report.meta.from, report.meta.to);
  const kpis = h("div", { cls: "kpis", testid: "reports-kpis" }, [
    kpi(
      "Net sales",
      formatMoney(totals.netCents),
      prior === null
        ? plainDetail("")
        : deltaEl(percentChange(totals.netCents, prior.netCents), windowText),
      "reports-kpi-net",
    ),
    kpi(
      "Tickets",
      formatCount(totals.tickets),
      prior === null
        ? plainDetail("")
        : deltaEl(percentChange(totals.tickets, prior.tickets), windowText),
      "reports-kpi-tickets",
    ),
    kpi(
      "Average ticket",
      avgTicketText(totals.avgTicketCents),
      prior === null || totals.avgTicketCents === null || prior.avgTicketCents === null
        ? plainDetail("")
        : deltaEl(percentChange(totals.avgTicketCents, prior.avgTicketCents), windowText),
      "reports-kpi-avg",
    ),
    kpi(
      "Tips",
      formatMoney(totals.tipsCents),
      prior === null
        ? plainDetail("")
        : deltaEl(percentChange(totals.tipsCents, prior.tipsCents), windowText),
      "reports-kpi-tips",
    ),
  ]);
  bodyEl.append(kpis);

  if (report.days.length <= 31) {
    bodyEl.append(barsCard(report));
  }

  // CASH1-S4: Card and Cash (money collected by each tender) appear only once some day in the range took cash.
  const tenders = showTenderColumns(report.days);
  const { wrap, tbody } = table(
    [
      { text: "Business day", left: true, sticky: true },
      { text: "Tickets" },
      { text: "Gross sales" },
      { text: "Discounts" },
      { text: "Comps" },
      { text: "Refunds" },
      { text: "Net sales" },
      { text: "Avg ticket" },
      { text: "Tax" },
      { text: "Tips" },
      ...(tenders ? [{ text: "Card" }, { text: "Cash" }] : []),
    ],
    "reports-day-table",
  );
  for (const day of report.days) {
    const tr = h("tr", { testid: "reports-day-row", attrs: { "data-date": day.businessDate } });
    tr.append(
      h("td", { cls: "l sticky" }, [
        h("div", { cls: "dlab" }, [
          h("span", { text: dayLabel(day.businessDate) }),
          h("small", { text: day.status === "final" ? "Final" : "Not final yet" }),
        ]),
      ]),
      h("td", { text: formatCount(day.tickets) }),
      h("td", { text: formatMoney(day.grossCents) }),
      moneyTd(reductionCell(day.discountCents)),
      moneyTd(reductionCell(day.compCents)),
      moneyTd(reductionCell(day.refundCents)),
      h("td", { cls: "strong", text: formatMoney(day.netCents) }),
      h("td", { text: avgTicketText(day.avgTicketCents) }),
      h("td", { text: formatMoney(day.taxCents) }),
      moneyTd(amountCell(day.tipsCents, true)),
      ...(tenders
        ? [h("td", { text: formatMoney(day.cardCents) }), h("td", { text: formatMoney(day.cashCents) })]
        : []),
    );
    tbody.append(tr);
  }
  tbody.append(
    h("tr", { cls: "total", testid: "reports-day-total" }, [
      h("td", { cls: "l sticky", text: "Total" }),
      h("td", { text: formatCount(totals.tickets) }),
      h("td", { text: formatMoney(totals.grossCents) }),
      h("td", { text: formatMoney(-totals.discountCents) }),
      h("td", { text: formatMoney(-totals.compCents) }),
      h("td", { text: formatMoney(-totals.refundCents) }),
      h("td", { text: formatMoney(totals.netCents) }),
      h("td", { text: avgTicketText(totals.avgTicketCents) }),
      h("td", { text: formatMoney(totals.taxCents) }),
      h("td", { text: formatMoney(totals.tipsCents) }),
      ...(tenders
        ? [h("td", { text: formatMoney(totals.cardCents) }), h("td", { text: formatMoney(totals.cashCents) })]
        : []),
    ]),
  );

  const memo = h("div", { cls: "memo", testid: "reports-memo" });
  memo.append(
    h("span", {}, [
      h("b", { text: "Net sales" }),
      " = gross sales minus discounts, comps and refunds. Tax and tips are not part of sales.",
    ]),
  );
  const notCounted: string[] = [];
  if (report.memo.voids.count > 0) {
    notCounted.push(
      `${formatCount(report.memo.voids.count)} voided ${report.memo.voids.count === 1 ? "drink" : "drinks"} worth ${formatMoney(report.memo.voids.valueCents)} (never charged).`,
    );
  }
  if (report.memo.walkouts.count > 0) {
    // CORE1: a walked tab collects nothing, so it is in no sales column; said here so the owner can total it.
    const n = report.memo.walkouts.count;
    notCounted.push(
      `${formatCount(n)} walked ${n === 1 ? "tab" : "tabs"} worth ${formatMoney(report.memo.walkouts.owedCents)} (never collected${report.memo.walkouts.lateCount > 0 ? `; ${formatCount(report.memo.walkouts.lateCount)} walked late, worth ${formatMoney(report.memo.walkouts.lateOwedCents)}` : ""}).`,
    );
  }
  if (report.memo.walkouts.legacyFinalized) {
    notCounted.push("Some older finalized days did not record Walkouts. Their figures are current, not historical.");
  }
  if (report.memo.openTabsCarried.count > 0) {
    const n = report.memo.openTabsCarried.count;
    notCounted.push(
      `${formatCount(n)} open ${n === 1 ? "tab" : "tabs"} worth ${formatMoney(report.memo.openTabsCarried.totalCents)} ${n === 1 ? "is" : "are"} carried and will count on the day ${n === 1 ? "it is" : "they are"} paid.`,
    );
  }
  if (notCounted.length > 0) {
    memo.append(
      h("span", { testid: "reports-not-counted" }, [
        h("b", { text: "Not counted in net sales:" }),
        ` ${notCounted.join(" ")}`,
      ]),
    );
  }
  memo.append(
    tieOutLine(
      report.tieOut,
      "Each day matches its Closeout Summary.",
      differenceText("Sales before tax"),
    ),
  );
  const needsLook = report.memo.pendingPayments + report.memo.failedPayments;
  if (needsLook > 0) {
    const parts: string[] = [];
    if (report.memo.pendingPayments > 0) {
      const n = report.memo.pendingPayments;
      parts.push(
        `${n} ${n === 1 ? "payment is" : "payments are"} still waiting on the processor and ${n === 1 ? "is" : "are"} not counted yet.`,
      );
    }
    if (report.memo.failedPayments > 0) {
      const n = report.memo.failedPayments;
      parts.push(
        `${n} ${n === 1 ? "payment" : "payments"} failed and ${n === 1 ? "is" : "are"} not counted.`,
      );
    }
    const line = h("span", { testid: "reports-needs-look" }, [
      h("span", {
        cls: "tag warn",
        text: `${needsLook} ${needsLook === 1 ? "item needs" : "items need"} a look`,
      }),
      ` ${parts.join(" ")} Review ${needsLook === 1 ? "it" : "them"} in `,
    ]);
    const link = h("button", {
      cls: "btn-quiet",
      text: "Closeout Summary",
      testid: "reports-open-closeout",
      attrs: { type: "button" },
    });
    link.addEventListener("click", () => {
      // The review button keeps its own permission gate in main.ts; this only
      // follows it, so a person who may not open the Closeout Summary cannot.
      const closeoutButton = document.querySelector<HTMLButtonElement>(
        "#admin-open-closeout-summary",
      );
      if (closeoutButton !== null && !closeoutButton.hidden) {
        closeoutButton.click();
      }
    });
    line.append(link);
    memo.append(line);
  }
  const card = h("div", { cls: "card" }, [h("h2", {}, ["Day by day"]), wrap, memo]);
  bodyEl.append(card);
  if (discounts !== undefined) bodyEl.append(discountKindCard(discounts));
  bodyEl.append(
    h("p", {
      cls: "foot",
      text: "Sales count on the day the card was charged, so a tab paid Saturday for a Friday visit counts as Saturday.",
    }),
  );
}

/** DSC21 (P0.21): what discounts cost this location, each marked Local or Corporate. */
function discountKindCard(report: DiscountKindReportDto): HTMLElement {
  const { wrap, tbody } = table(
    [
      { text: "Discount", left: true },
      { text: "Kind" },
      { text: "Applied" },
      { text: "Cost" },
    ],
    "reports-discount-table",
  );
  for (const row of report.rows) {
    tbody.append(
      h("tr", { testid: "reports-discount-row", attrs: { "data-kind": row.kind } }, [
        h("td", { cls: "l", text: row.name }),
        h("td", { testid: "reports-discount-kind", text: discountKindLabel(row.kind) }),
        h("td", { text: formatCount(row.applications) }),
        h("td", { text: formatMoney(-row.costCents) }),
      ]),
    );
  }
  if (report.rows.length === 0) {
    tbody.append(h("tr", {}, [h("td", { cls: "l", text: "No rows to show for this period.", attrs: { colspan: "4" } })]));
  } else {
    tbody.append(
      h("tr", { cls: "total", testid: "reports-discount-total" }, [
        h("td", { cls: "l", text: "Total" }),
        h("td", { text: "" }),
        h("td", { text: formatCount(report.totals.applications) }),
        h("td", { text: formatMoney(-report.totals.costCents) }),
      ]),
    );
  }
  const head = h(
    "div",
    { style: { display: "flex", "align-items": "center", "justify-content": "space-between", gap: "12px" } },
    [h("h2", {}, [`Discounts at ${report.meta.locationName}`])],
  );
  const session = state.session;
  // EXP1-S4 (DEC-402): the card exports on its own (CSV, Excel, PDF), audited with its format; it needs report.export.
  if (session !== undefined && hasPermission(session, "report.export") && report.rows.length > 0) {
    const trigger = h("button", { cls: "btn-secondary", testid: "reports-discount-export", text: "Export", attrs: { type: "button" } }) as HTMLButtonElement;
    head.append(trigger);
    createExportMenu({
      trigger,
      testIdPrefix: "reports-discount-export",
      menuActive: () => true,
      googleSheet: () => state.session,
      onPick: (format) => void handleDiscountDownload(report, format),
      onPlain: () => void handleDiscountDownload(report, "csv"),
    });
  }
  return h("div", { cls: "card", testid: "reports-discount-card" }, [head, wrap]);
}

async function handleDiscountDownload(report: DiscountKindReportDto, format: GateExportFormat): Promise<void> {
  const session = state.session;
  if (session === undefined) return;
  downloadStatusEl.textContent = "";
  const result = await getDiscountKindFile(
    session.apiBaseUrl,
    session.tenantId,
    report.meta.from,
    report.meta.to,
    format,
    report.meta.locationId,
  );
  if (result.status !== 200 || result.blob === undefined) {
    downloadStatusEl.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(downloadStatusEl, result.blob, `discounts_${report.meta.from}_${report.meta.to}`, format);
}

function barsCard(report: SalesByDayReportDto): HTMLElement {
  const max = Math.max(1, ...report.days.map((d) => Math.max(0, d.netCents)));
  const bars = h("div", { cls: "bars", testid: "reports-bars" });
  const labelEvery = report.days.length > 14 ? Math.ceil(report.days.length / 14) : 1;
  report.days.forEach((day, index) => {
    const height = Math.round((Math.max(0, day.netCents) / max) * 100);
    const bar = h("div", {
      cls: `bar${day.status === "open" ? " nf" : ""}`,
      testid: "reports-bar",
      attrs: { "data-date": day.businessDate },
    });
    const showLabel = index % labelEvery === 0;
    bar.append(
      h("span", {
        text: report.days.length <= 14 ? formatMoney(day.netCents).replace(/\.00$/, "") : "",
      }),
      h("i", { style: { height: `${Math.max(height, 2)}%` } }),
      h("em", { text: showLabel ? weekdayLabel(day.businessDate) : "" }),
    );
    bars.append(bar);
  });
  return h("div", { cls: "card" }, [
    h("h2", {}, [
      "Net sales per day ",
      h("small", { text: "Striped bar: the day is not final yet and can still change" }),
    ]),
    bars,
  ]);
}

// ---------------------------------------------------------------------
// Download
// ---------------------------------------------------------------------

async function handleCostDownload(file: "drinks" | "days", format: GateExportFormat = "csv"): Promise<void> {
  const session = state.session;
  if (session === undefined || state.from === "") return;
  const gen = state.generation;
  downloadStatusEl.textContent = "";
  const result = await getCostMarginCsv(
    session.apiBaseUrl,
    session.tenantId,
    state.from,
    state.to,
    costQuery(),
    file,
    format,
  );
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.blob === undefined) {
    downloadStatusEl.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(downloadStatusEl, result.blob, `${file === "days" && format === "csv" ? "cost-and-margin-by-day" : "cost-and-margin"}_${state.from}_${state.to}`, format);
}

async function handleMenuProfitDownload(format: GateExportFormat = "csv"): Promise<void> {
  const session = state.session;
  if (session === undefined || state.from === "") return;
  const gen = state.generation;
  downloadStatusEl.textContent = "";
  const result = await getMenuProfitabilityCsv(
    session.apiBaseUrl,
    session.tenantId,
    state.from,
    state.to,
    state.locationId,
    format,
  );
  if (gen !== state.generation) return;
  if (result.status !== 200 || result.blob === undefined) {
    downloadStatusEl.textContent = result.networkError
      ? "Could not download this file. Check your connection."
      : capitalizeServerMessage(result.message ?? "Could not download this file.");
    return;
  }
  await deliverExportFile(downloadStatusEl, result.blob, `menu-profitability_${state.from}_${state.to}`, format);
}

async function handleDownload(format: GateExportFormat = "csv", sheet?: string): Promise<void> {
  const session = state.session;
  const report = state.report;
  if (session === undefined || report === undefined) return;
  if (report === "custom") {
    await handleCustomDownload(session, format);
    return;
  }
  if (report === "abc-spirits") {
    downloadButton.disabled = true;
    try {
      await handleAbcDownload(session, format, sheet);
    } finally {
      downloadButton.disabled = false;
    }
    return;
  }
  if (report === "sales-tax") {
    downloadButton.disabled = true;
    try {
      await handleSalesTaxDownload(session, format);
    } finally {
      downloadButton.disabled = false;
    }
    return;
  }
  if (state.from === "") return;
  if (report === "cost-margin") {
    downloadButton.disabled = true;
    try {
      await handleCostDownload(sheet === "days" ? "days" : "drinks", format);
    } finally {
      downloadButton.disabled = false;
    }
    return;
  }
  if (report === "menu-profitability") {
    downloadButton.disabled = true;
    try {
      await handleMenuProfitDownload(format);
    } finally {
      downloadButton.disabled = false;
    }
    return;
  }
  const gen = state.generation;
  downloadStatusEl.textContent = "";
  downloadButton.disabled = true;
  try {
    const result = await getReportCsv(
      session.apiBaseUrl,
      session.tenantId,
      report,
      state.from,
      state.to,
      report === "sales-by-day" || isInventoryRange(report) ? state.locationId : undefined,
      format,
    );
    if (gen !== state.generation) return;
    if (result.status !== 200 || result.blob === undefined) {
      downloadStatusEl.textContent = result.networkError
        ? "Could not download this file. Check your connection."
        : capitalizeServerMessage(result.message ?? "Could not download this file.");
      return;
    }
    await deliverExportFile(downloadStatusEl, result.blob, `${report}_${state.from}_${state.to}`, format);
  } finally {
    downloadButton.disabled = false;
  }
}

// ---------------------------------------------------------------------
// Wiring (once, at module load)
// ---------------------------------------------------------------------

for (const [id, button] of Object.entries(NAV_BUTTONS) as Array<[ReportId, HTMLButtonElement]>) {
  button.addEventListener("click", () => void showReport(id));
}
customApplyButton.addEventListener("click", () => handleCustomApply());
locationSelectEl.addEventListener("change", () => {
  state.locationId = locationSelectEl.value;
  if (state.report === "sales-by-day" || isInventoryRange(state.report)) {
    void ensureContextAndRange().then(() => loadReport());
  } else {
    void loadReport();
  }
});
